import React from 'react';
import { Button, InputNumber, Select } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';

export type PricingType = 'RATIO' | 'FIXED';

export interface TierRow {
  startOrderCount: number | null;
  endOrderCount: number | null;
  pricingType: PricingType;
  discountRatio: number | null;
  fixedAmount: number | null;
}

export const DEFAULT_TIERS: TierRow[] = [
  { startOrderCount: 0, endOrderCount: 99999999, pricingType: 'RATIO', discountRatio: null, fixedAmount: null },
];

/** 只读场景（绑定/解绑/编辑详情预览）展示某一梯度的折扣类型+值；pricingType 缺失（历史数据）按 RATIO 展示 */
export function formatTierDiscountDisplay(tier: {
  pricingType?: string | null;
  discountRatio?: number | null;
  fixedAmount?: number | null;
}): string {
  if (tier.pricingType === 'FIXED') {
    return `按固定金额 $${tier.fixedAmount ?? '-'}/单`;
  }
  return `按折扣比例 ${tier.discountRatio ?? '-'}%`;
}

const PRICING_TYPE_OPTIONS: { value: PricingType; label: string }[] = [
  { value: 'RATIO', label: '按折扣比例' },
  { value: 'FIXED', label: '按固定金额(结果金额)' },
];

interface CostPeriodicTierConfigProps {
  tiers: TierRow[];
  onChange: (tiers: TierRow[]) => void;
  readOnly?: boolean;
  discountWidth?: number;
}

const CostPeriodicTierConfig: React.FC<CostPeriodicTierConfigProps> = ({
  tiers,
  onChange,
  readOnly = false,
  discountWidth = 380,
}) => {
  const handleAddRow = () => {
    const last = tiers[tiers.length - 1];
    onChange([
      ...tiers.slice(0, -1),
      { startOrderCount: last.startOrderCount, endOrderCount: null, pricingType: 'RATIO', discountRatio: null, fixedAmount: null },
      { ...last, startOrderCount: null },
    ]);
  };

  const handleRemoveLast = () => {
    if (tiers.length <= 1) return;
    const lastRow = tiers[tiers.length - 1];
    const prevEnd = tiers.length >= 3 ? tiers[tiers.length - 3].endOrderCount : 0;
    onChange([...tiers.slice(0, -2), { ...lastRow, startOrderCount: prevEnd }]);
  };

  const handleEndChange = (index: number, value: number | null) => {
    const next = tiers.map((row, i) => {
      if (i === index) return { ...row, endOrderCount: value };
      // 联动：下一行的 startOrderCount 跟随当前行 endOrderCount
      if (i === index + 1) return { ...row, startOrderCount: value };
      return row;
    });
    onChange(next);
  };

  const handlePricingTypeChange = (index: number, value: PricingType) => {
    onChange(tiers.map((row, i) => (i === index ? { ...row, pricingType: value } : row)));
  };

  const handleDiscountRatioChange = (index: number, value: number | null) => {
    onChange(tiers.map((row, i) => (i === index ? { ...row, discountRatio: value } : row)));
  };

  const handleFixedAmountChange = (index: number, value: number | null) => {
    onChange(tiers.map((row, i) => (i === index ? { ...row, fixedAmount: value } : row)));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {tiers.map((tier, index) => {
        const isLast = index === tiers.length - 1;
        const isOnly = tiers.length === 1;
        return (
          <div key={index} style={{ display: 'flex', alignItems: 'center' }}>
            {/* 阶梯输入区 */}
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
              {/* 起始单量：始终 disabled（第一行固定0，其余联动） */}
              <InputNumber
                value={tier.startOrderCount}
                style={{ width: 115 }}
                disabled
              />
              <span style={{ margin: '0 4px' }}>-</span>
              {/* 截止单量：最后一行固定不可编辑 */}
              <InputNumber
                value={tier.endOrderCount}
                min={0}
                max={99999999}
                precision={0}
                style={{ width: 115 }}
                disabled={readOnly || isLast}
                onChange={v => handleEndChange(index, v)}
              />
              <span style={{ margin: '0 6px' }}>单</span>
              {/* 操作按钮：只在最后一行显示 */}
              {!readOnly && isLast && (
                <>
                  <Button
                    type="text"
                    icon={<PlusOutlined />}
                    size="small"
                    onClick={handleAddRow}
                    style={{ padding: '0 4px' }}
                  />
                  {!isOnly && (
                    <Button
                      type="text"
                      icon={<MinusOutlined />}
                      size="small"
                      danger
                      onClick={handleRemoveLast}
                      style={{ padding: '0 4px' }}
                    />
                  )}
                </>
              )}
            </div>
            {/* 折扣类型 + 折扣比例/固定金额：固定宽度，与列头对齐（左对齐）；不换行，避免下拉/单位挤到下一行 */}
            <div style={{ width: discountWidth, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 8, flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
              <Select<PricingType>
                value={tier.pricingType}
                options={PRICING_TYPE_OPTIONS}
                disabled={readOnly}
                style={{ width: 200, flexShrink: 0 }}
                onChange={v => handlePricingTypeChange(index, v)}
              />
              {tier.pricingType === 'FIXED' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  <span>$</span>
                  <InputNumber
                    value={tier.fixedAmount}
                    min={0}
                    precision={2}
                    style={{ width: 100 }}
                    disabled={readOnly}
                    onChange={v => handleFixedAmountChange(index, v)}
                  />
                  <span>/单</span>
                </span>
              ) : (
                <span style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, whiteSpace: 'nowrap' }}>
                  {/* 与 FIXED 分支的 $ 前缀同宽（同一字符隐藏占位），保证切换类型时输入框位置不跑偏 */}
                  <span style={{ visibility: 'hidden' }}>$</span>
                  <InputNumber
                    value={tier.discountRatio}
                    min={0}
                    max={100}
                    precision={2}
                    style={{ width: 100 }}
                    disabled={readOnly}
                    onChange={v => handleDiscountRatioChange(index, v)}
                  />
                  <span>%</span>
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default CostPeriodicTierConfig;
